import { useId, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import type { PasoPuestaEnMarcha } from "../datos/pasosPuestaEnMarcha";
import { CLASE_FOCO } from "./tarjetasFuente";

/**
 * El pie de CUALQUIER paso de la Puesta en marcha: el botón de continuar y la
 * línea que explica qué pasa con él.
 *
 * Es compartido a propósito. Cada paso decide con su propia regla si se puede
 * seguir (el de conexiones, con `continuarPuestaEnMarcha.ts`), pero las dos
 * cosas que no pueden salir mal se resuelven UNA sola vez, acá:
 *
 *  1. DESHABILITADO DICE POR QUÉ. Nunca se dibuja un botón gris y mudo: si
 *     `habilitado` es `false`, `motivo` es obligatorio y se muestra al lado.
 *     Un usuario que no sabe qué le falta se queda mirando la pantalla.
 *  2. HABILITADO NO MIENTE. Mientras `siguiente` sea `null` —hoy, con un solo
 *     paso construido— el botón NO lleva a ningún lado, y eso se dice ANTES de
 *     apretarlo, no después. Es el mismo criterio que el resto del bloque usa
 *     con los archivos que no se suben: el aviso va arriba de la acción,
 *     porque enterarse después no sirve de nada.
 *
 * Y el clic tampoco es un no-op: sin paso siguiente, lleva el FOCO al aviso.
 * Un botón enfocable que al apretarse no cambia nada en ningún lado deja a
 * quien navega con teclado o lector de pantalla sin ninguna respuesta; así, el
 * lector lee la explicación de por qué no pasó nada.
 *
 * Ver ADR-2026-08-17-estudio-marketing-puesta-en-marcha.
 */

/** Lo que el contenedor le pasa a CADA paso. Es lo mínimo que un paso necesita
 *  saber del flujo: qué hay a cada lado (o nada) y cómo moverse. */
export interface PropsPasoPuestaEnMarcha {
  siguiente: PasoPuestaEnMarcha | null;
  onContinuar: (siguiente: PasoPuestaEnMarcha) => void;
  /** `null` en el primer paso: ahí no se dibuja "Volver". */
  anterior: PasoPuestaEnMarcha | null;
  onVolver: (anterior: PasoPuestaEnMarcha) => void;
}

export interface AvisoPie {
  texto: string;
  /** `alerta` pinta el rojo de error. Se reserva para lo que salió mal (una
   *  consulta que no respondió), no para lo que falta hacer. */
  tono: "neutro" | "alerta";
}

interface PropsPie extends PropsPasoPuestaEnMarcha {
  habilitado: boolean;
  /** Por qué no se puede seguir. Obligatorio cuando `habilitado` es `false`:
   *  el tipo lo pide siempre para que nadie pueda "olvidárselo". */
  motivo: AvisoPie | null;
}

export const PieDePaso = ({
  habilitado,
  motivo,
  siguiente,
  onContinuar,
  anterior,
  onVolver,
}: PropsPie) => {
  const avisoRef = useRef<HTMLParagraphElement>(null);
  const idAviso = useId();

  /** Habilitado y sin paso siguiente: el caso de HOY. */
  const sinDestino = habilitado && siguiente === null;
  /** Hay algo escrito al lado del botón que lo explica (y que por lo tanto
   *  puede ser su descripción accesible). */
  const hayAviso = (!habilitado && motivo !== null) || sinDestino;

  return (
    // Sin `border-t`: la línea divisoria la sacó Matías por pedido explícito.
    // NO REVERTIR. El `mt-auto` es lo que manda el pie al fondo de la pantalla.
    <div className="mt-auto flex flex-wrap items-center justify-end gap-4 pt-6">
      {/* `role="status"`: el motivo cambia solo cuando vuelve la consulta de las
          conexiones, sin que el usuario toque nada. Sin esto, un lector de
          pantalla se queda con el primer texto que se renderizó. */}
      <div className="min-w-0 flex-1" role="status">
        {/* El motivo del bloqueo ("Conectá Instagram para seguir") ya no se
            dibuja: lo sacó Matías por pedido explícito. NO REVERTIR el texto
            visible. Se conserva como `aria-describedby` del botón —abajo, en un
            `sr-only`— porque un botón deshabilitado y mudo deja sin ninguna
            explicación a quien usa lector de pantalla, y eso no es una decisión
            de diseño visual. */}
        {!habilitado && motivo && (
          <p id={idAviso} className="sr-only">
            {motivo.texto}
          </p>
        )}
        {sinDestino && (
          <p
            id={idAviso}
            ref={avisoRef}
            // Enfocable por código (lo enfoca el clic del botón) pero fuera de
            // la rueda de tabulación: no es una parada más, es el destino del
            // botón que no tiene destino.
            tabIndex={-1}
            className="flex items-start gap-2 rounded-xl bg-[#F7EBD5] py-3 px-4 text-[12.5px] leading-relaxed text-[#8A5A08] outline-none"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Ya podés seguir, pero{" "}
              <strong className="font-semibold">el paso que sigue todavía no existe</strong>: es la
              próxima pantalla de la puesta en marcha y está sin construir. Continuar no te lleva a
              ningún lado por ahora.
            </span>
          </p>
        )}
      </div>

      {/* "Volver" sólo desde el segundo paso en adelante. NO es adorno: el paso
          se guarda en estado y el ítem de la sidebar no remonta la pantalla, así
          que sin esto quien avanza queda encerrado en el último paso hasta
          recargar. Secundario y sin fondo: la acción de este pie es Continuar. */}
      {anterior && (
        <button
          type="button"
          onClick={() => onVolver(anterior)}
          className={`shrink-0 rounded-xl py-3 px-5 text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.55)] transition-colors hover:bg-[#F0F2F7] hover:text-brand-black ${CLASE_FOCO}`}
        >
          Volver
        </button>
      )}

      <button
        type="button"
        disabled={!habilitado}
        // El botón nunca queda solo: lo que se ve a la izquierda (el motivo o
        // el aviso) es su descripción accesible, así que un lector de pantalla
        // lee "Continuar, no disponible, conectá Instagram para seguir" en vez
        // de un botón deshabilitado sin explicación.
        aria-describedby={hayAviso ? idAviso : undefined}
        onClick={() => {
          if (siguiente) {
            onContinuar(siguiente);
            return;
          }
          // No hay a dónde ir. En vez de no hacer NADA, el clic manda el foco
          // al aviso que explica por qué.
          avisoRef.current?.focus();
        }}
        // Padding 2:1 (py-3 / px-6) y radio 12: la misma forma que el resto de
        // los primarios de la sección.
        className={`shrink-0 rounded-xl bg-brand-primary py-3 px-6 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${CLASE_FOCO}`}
      >
        Continuar
      </button>
    </div>
  );
};

export default PieDePaso;
